import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import SystemSettings from '../../components/admin/SystemSettings';
import ShiftChangeStatus from '../../components/admin/ShiftChangeStatus';
import { canAccessMonthlyFixedChargeWriteOff } from '../../utils/monthlyFixedChargeAdmin';

const AdminDashboard = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  // Check if user has access to this page
  useEffect(() => {
    const checkAccess = () => {
      setLoading(true);
      
      // If not logged in, redirect to home
      if (!user) {
        navigate('/');
        return;
      }
      
      // Check if user has any staff role
      const isStaff = hasRole('ADMIN') || hasRole('MANAGEMENT') || hasRole('OPERATOR');
      
      // If user doesn't have any staff role, redirect them to the regular dashboard
      if (!isStaff) {
        navigate('/dashboard');
        return;
      }
      
      setLoading(false);
    };

    checkAccess();
  }, [user, hasRole, navigate]);

  // Render different tabs based on user role
  const renderTabs = () => {
    return (
      <div className="border-b border-gray-200 mb-6">
        <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
          <li className="mr-2">
            <button
              className={`inline-block p-4 rounded-t-lg ${
                activeTab === 'dashboard'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('dashboard')}
            >
              Dashboard Overview
            </button>
          </li>
          
          {/* Only ADMIN and MANAGEMENT can access system settings */}
          {(hasRole('ADMIN') || hasRole('MANAGEMENT')) && (
            <li className="mr-2">
              <button
                className={`inline-block p-4 rounded-t-lg ${
                  activeTab === 'settings'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('settings')}
              >
                System Settings
              </button>
            </li>
          )}
        </ul>
      </div>
    );
  };

  // Render dashboard overview with statistics
  const renderDashboardOverview = () => {
    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Total Users</h3>
            <p className="text-3xl font-bold text-blue-600">--</p>
            <p className="text-sm text-gray-500 mt-1">Statistics coming soon</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Pending Approvals</h3>
            <p className="text-3xl font-bold text-yellow-600">--</p>
            <Link to="/user-management" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
              Manage users →
            </Link>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-2">System Status</h3>
            <p className="text-xl font-medium text-green-600">
              <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
              Operational
            </p>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          <div className="text-center py-8 text-gray-500">
            <p>Detailed activity statistics will be available soon.</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-4">
          <Link to="/user-management" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
            Manage Users
          </Link>
          
          {(hasRole('ADMIN') || hasRole('MANAGEMENT')) && (
            <Link to="/site-management" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700">
              Manage Sites
            </Link>
          )}

          {hasRole('ADMIN') && (
            <Link to="/endpoint-test" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700">
              API Endpoint Test
            </Link>
          )}

          {hasRole('ADMIN') && canAccessMonthlyFixedChargeWriteOff(user) && (
            <Link to="/admin/monthly-fixed-charge-write-offs" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700">
              Fixed Charge Write-Offs
            </Link>
          )}
        </div>
      </div>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboardOverview();
      case 'settings':
        return <SystemSettings />;
      default:
        return renderDashboardOverview();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-none md:w-1/3">
            <h1 className="text-2xl font-bold mb-2">Admin Dashboard</h1>
            <div className="flex items-center">
              <span className="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {user?.roles.join(', ')}
              </span>
              <p className="text-gray-600">
                Welcome back, {user?.firstName} {user?.lastName}
              </p>
            </div>
          </div>
          <div className="flex-grow md:w-2/3">
            <ShiftChangeStatus inline={true} />
          </div>
        </div>
      </div>
      
      {/* Tab navigation */}
      {renderTabs()}
      
      {/* Tab content */}
      <div className="bg-white rounded-lg p-6 shadow">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdminDashboard; 